import type { ReactNode } from "react";
import { PushPin, Verified } from "@/components/ui/icons";
import { CoworkerIcon } from "@/components/ui/coworker-icon";
import { Badge } from "@/components/ui/badge";
import { isAnoTeamMember } from "@/lib/render-user";
import { TEXT_MESSAGE_AUTHOR } from "@/lib/surface-styles";
import { cn } from "@/lib/utils";

interface MessageHeaderProps {
  userId: string;
  displayName: string;
  user?: {
    id: string;
    display_name: string;
    avatar_url?: string | null;
    status_emoji?: string | null;
    is_deactivated?: boolean | null;
    is_coworker?: boolean | null;
    email?: string | null;
  } | null;
  isCoworkerAuthored: boolean;
  // When true, render a "VIA CLI" pill next to the author name. Set by
  // `Message.tsx` when the message came through MCP-HTTP (`metadata.agent`)
  // from a human author (i.e. NOT a coworker) and isn't an automation
  // engine write. Distinguishes "human used `ano` CLI" from "human used
  // the desktop app" — server-side metadata is identical otherwise.
  viaCli?: boolean;
  senderIsFormerMember: boolean;
  time: string;
  agentIndicator: ReactNode;
  isPinned?: boolean;
  isEdited?: boolean;
  onUserClick?: (userId: string) => void;
}

/**
 * Header row above the message body — display name, badges (Ano team /
 * status emoji / deactivated / former-member), time, agent indicator,
 * pinned + edited markers. Only rendered in non-compact mode.
 */
export function MessageHeader({
  userId,
  displayName,
  user,
  isCoworkerAuthored,
  viaCli = false,
  senderIsFormerMember,
  time,
  agentIndicator,
  isPinned,
  isEdited,
  onUserClick,
}: MessageHeaderProps) {
  // External agents are surfaced two ways: by the per-message
  // `metadata.coworker` flag (set by the in-process scheduler in
  // `agent-scheduler-coworker-turn.ts`) AND by the author's `is_coworker`
  // user-table field (set when the coworker user is created). Either
  // signal shows the Cloud icon — without this, an external agent that
  // posts via the MCP HTTP API gets `metadata.agent=true` but no
  // `metadata.coworker`, so the old single-source check missed them.
  const showCoworkerIcon = isCoworkerAuthored || user?.is_coworker === true;
  return (
    <div data-message-header className="flex items-baseline gap-2 mb-1">
      <span
        className={cn("cursor-pointer hover:underline", TEXT_MESSAGE_AUTHOR)}
        onClick={(e) => {
          e.stopPropagation();
          onUserClick?.(userId);
        }}
      >
        {displayName}
      </span>
      {showCoworkerIcon && <CoworkerIcon size={12} className="shrink-0" />}
      {viaCli && (
        <Badge
          variant="secondary"
          className="rounded-full border-transparent bg-foreground/[0.06] px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-muted-foreground font-normal"
          title="Sent via the ano CLI"
        >
          via CLI
        </Badge>
      )}
      {user && isAnoTeamMember(user) ? (
        // Ano team verified badge — Solar's purpose-built `VerifiedCheck`
        // (starburst shape with a checkmark) in the brand green
        // (`text-primary`). Replaces the previous custom 8-sided
        // clipPath span + nested CheckFat — same semantics, cleaner
        // visual identity, no hand-rolled SVG geometry.
        <span
          title="Ano team"
          aria-label="Ano team"
          className="inline-flex shrink-0"
        >
          <Verified size={14} className="text-primary" />
        </span>
      ) : user?.status_emoji ? (
        <span className="text-sm" title={user.status_emoji}>
          {user.status_emoji}
        </span>
      ) : null}
      {user?.is_deactivated ? (
        <Badge
          variant="secondary"
          className="rounded-full border-transparent bg-muted/50 px-1.5 py-0.5 text-[10px] text-muted-foreground/50 font-normal"
          title="This account has been deactivated"
        >
          Deactivated
        </Badge>
      ) : senderIsFormerMember ? (
        <Badge
          variant="secondary"
          className="rounded-full border-transparent bg-muted/50 px-1.5 py-0.5 text-[10px] text-muted-foreground/50 font-normal"
          title="No longer a member of this workspace"
        >
          Former member
        </Badge>
      ) : null}
      <span className="text-[11px] text-muted-foreground">{time}</span>
      {agentIndicator}
      {isPinned && (
        <span className="text-[10px] text-warning flex items-center gap-0.5">
          <PushPin size={10} weight="Bold" />
          pinned
        </span>
      )}
      {isEdited && (
        <span className="text-[10px] text-muted-foreground/50">(edited)</span>
      )}
    </div>
  );
}
